import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Phone,
  Bot,
  Link as LinkIcon,
  User,
  Mail,
  Map,
  ListTodo,
  MessageSquareCode,
  CreditCard,
  Users,
  CalendarCheck,
  BarChart3
} from 'lucide-react';
import { BrandingSettings } from '@/types/branding';


export default function PreviewDashboard({ branding }: { branding: BrandingSettings }) {
  const clientNavigation = [
    { name: 'Dashboard', icon: LayoutDashboard, active: true },
    { name: 'Scheduled Calls', icon: ListTodo },
    { name: 'Past Calls', icon: Phone },
    { name: 'Email Management', icon: Mail },
    { name: 'AI Employee Settings', icon: Bot },
    { name: 'Hero Commands™', icon: MessageSquareCode },
    { name: 'Integrations', icon: LinkIcon },
    { name: 'Billing', icon: CreditCard },
    { name: 'Account', icon: User },
    { name: 'Resources', icon: Map },
  ];

  return (
    <div className="min-h-screen bg-muted" style={{ backgroundColor: branding.colors.background }}>
      {/* Sidebar */}
      <div className="flex h-screen">
        <div className="flex h-full w-64 flex-col bg-white border-r border-border">
          {/* Logo/Header */}
          <div className="flex items-center justify-center px-4 py-0 border-b border-border">
            <img
              src={branding.logoUrl || "https://i.imgur.com/VAuAJzp.png"}
              alt={branding.logoUrl ? branding.companyName : "AI Employee Logo"}
              className="h-40 w-auto max-w-full object-contain"
              onError={(e) => {
                // Fallback to default logo if partner logo fails to load
                const target = e.target as HTMLImageElement;
                target.src = "https://i.imgur.com/VAuAJzp.png";
                target.alt = "AI Employee Logo";
              }}
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-4">
            {/* Client Navigation */}
            <div className="space-y-1">
              {clientNavigation.map((item) => (
                <div
                  key={item.name}
                  className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    item.active
                      ? 'bg-primary/10 text-primary border-r-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  style={item.active ? {
                    backgroundColor: `${branding.colors.primary}20`,
                    color: branding.colors.primary,
                    borderRightColor: branding.colors.primary
                  } : {}}
                >
                  <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {item.name}
                </div>
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            {/* User Info */}
            <div className="bg-accent/50 rounded-lg p-3 mb-3 transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center text-sm font-medium text-foreground mb-1">
                    <div className="h-2 w-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                    <span className="truncate">John Doe</span>
                  </div>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Mail className="w-3 h-3 mr-1.5 flex-shrink-0" />
                    <span className="truncate">Online</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6 lg:p-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2" style={{ color: branding.colors.foreground }}>
                Dashboard
              </h1>
              <p className="text-lg text-muted-foreground">
                Welcome to {branding.companyName} - Your AI scheduling platform
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Calls Made</CardTitle>
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">1,234</div>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-green-600">+12%</span> from last month
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Calls Answered</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">987</div>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-green-600">+8%</span> from last month
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Appointments Booked</CardTitle>
                  <CalendarCheck className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">89</div>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-green-600">+15%</span> from last month
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Charts and Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Call Activity</CardTitle>
                  <CardDescription>Recent call patterns and trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-center justify-center bg-muted/50 rounded">
                    <BarChart3 className="h-8 w-8 text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground text-sm">Activity Chart</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Call Outcomes</CardTitle>
                  <CardDescription>Distribution of call results</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-center justify-center bg-muted/50 rounded">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-bold text-lg">65%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Successful calls</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Common tasks and shortcuts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Phone className="h-4 w-4 mr-2" />
                      Start New Call
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Mail className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <CalendarCheck className="h-4 w-4 mr-2" />
                      Schedule Appointment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Branding Theme Showcase */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: branding.colors.primary }}>Theme Colors</CardTitle>
                  <CardDescription>Your brand color palette in action</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: `${branding.colors.primary}15`, border: `1px solid ${branding.colors.primary}30` }}>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: branding.colors.primary }}></div>
                        <span className="font-medium" style={{ color: branding.colors.primary }}>Primary Color</span>
                      </div>
                      <span className="text-sm font-mono" style={{ color: branding.colors.primary }}>{branding.colors.primary}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: `${branding.colors.secondary}15`, border: `1px solid ${branding.colors.secondary}30` }}>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: branding.colors.secondary }}></div>
                        <span className="font-medium" style={{ color: branding.colors.secondary }}>Secondary Color</span>
                      </div>
                      <span className="text-sm font-mono" style={{ color: branding.colors.secondary }}>{branding.colors.secondary}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: `${branding.colors.accent}15`, border: `1px solid ${branding.colors.accent}30` }}>
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: branding.colors.accent }}></div>
                        <span className="font-medium" style={{ color: branding.colors.accent }}>Accent Color</span>
                      </div>
                      <span className="text-sm font-mono" style={{ color: branding.colors.accent }}>{branding.colors.accent}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle style={{ color: branding.colors.primary }}>UI Elements</CardTitle>
                  <CardDescription>Buttons and interactive components</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Button
                      className="w-full"
                      style={{
                        backgroundColor: branding.colors.primary,
                        color: '#FFFFFF',
                        border: `1px solid ${branding.colors.primary}`
                      }}
                    >
                      Primary Button
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      style={{
                        borderColor: branding.colors.secondary,
                        color: branding.colors.secondary
                      }}
                    >
                      Secondary Button
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      style={{
                        borderColor: branding.colors.accent,
                        color: branding.colors.accent
                      }}
                    >
                      Accent Button
                    </Button>
                    <div className="flex gap-2">
                      <Badge style={{ backgroundColor: `${branding.colors.primary}20`, color: branding.colors.primary, borderColor: branding.colors.primary }}>Active</Badge>
                      <Badge style={{ backgroundColor: `${branding.colors.secondary}20`, color: branding.colors.secondary, borderColor: branding.colors.secondary }}>Pending</Badge>
                      <Badge style={{ backgroundColor: `${branding.colors.accent}20`, color: branding.colors.accent, borderColor: branding.colors.accent }}>New</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle style={{ color: branding.colors.primary }}>Content Cards</CardTitle>
                  <CardDescription>Card layouts with your branding</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg border" style={{ borderColor: `${branding.colors.primary}30`, backgroundColor: `${branding.colors.primary}5` }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium" style={{ color: branding.colors.primary }}>Welcome Message</h4>
                          <p className="text-sm text-muted-foreground">{branding.customCopy.welcomeMessage}</p>
                        </div>
                        <Mail className="h-5 w-5" style={{ color: branding.colors.primary }} />
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ borderColor: `${branding.colors.secondary}30`, backgroundColor: `${branding.colors.secondary}5` }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium" style={{ color: branding.colors.secondary }}>AI Assistant</h4>
                          <p className="text-sm text-muted-foreground">{branding.customCopy.aiName}</p>
                        </div>
                        <Bot className="h-5 w-5" style={{ color: branding.colors.secondary }} />
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border" style={{ borderColor: `${branding.colors.accent}30`, backgroundColor: `${branding.colors.accent}5` }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium" style={{ color: branding.colors.accent }}>Call-to-Action</h4>
                          <p className="text-sm text-muted-foreground">{branding.customCopy.ctaText}</p>
                        </div>
                        <Phone className="h-5 w-5" style={{ color: branding.colors.accent }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity and Call Logs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>Latest system events and updates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">New appointment scheduled</p>
                        <p className="text-xs text-muted-foreground">2 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Call completed successfully</p>
                        <p className="text-xs text-muted-foreground">5 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Email sent to customer</p>
                        <p className="text-xs text-muted-foreground">15 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">AI Employee updated settings</p>
                        <p className="text-xs text-muted-foreground">1 hour ago</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Call Logs</CardTitle>
                  <CardDescription>Recent call history</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Phone className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">+1 (555) 123-4567</p>
                          <p className="text-xs text-muted-foreground">Appointment scheduled</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">2 min</p>
                        <Badge variant="secondary" className="text-xs">Completed</Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Phone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">+1 (555) 987-6543</p>
                          <p className="text-xs text-muted-foreground">Information provided</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">5 min</p>
                        <Badge variant="outline" className="text-xs">Answered</Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <Phone className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">+1 (555) 456-7890</p>
                          <p className="text-xs text-muted-foreground">No answer</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">1 min</p>
                        <Badge variant="destructive" className="text-xs">Missed</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Calls Section */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Calls</CardTitle>
                <CardDescription>Latest call activity and outcomes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Appointment scheduled</p>
                        <p className="text-xs text-muted-foreground">+1 (555) 123-4567 • 2 minutes ago</p>
                      </div>
                    </div>
                    <Badge variant="secondary">Completed</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Information requested</p>
                        <p className="text-xs text-muted-foreground">+1 (555) 987-6543 • 15 minutes ago</p>
                      </div>
                    </div>
                    <Badge variant="outline">Answered</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Call not answered</p>
                        <p className="text-xs text-muted-foreground">+1 (555) 456-7890 • 1 hour ago</p>
                      </div>
                    </div>
                    <Badge variant="destructive">Missed</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </div>
  );
}